import {
  ContainerType,
  ItemTypeCategory,
} from '../../../graphql/generatedTypes';
import { GetAllEventAccountStockQuery } from '../../../graphql/queries/getAllEventAccountStock.generated';
import { GetEventAccountStockQuery } from '../../../graphql/queries/getEventAccountStock.generated';
import { distinctBy } from '@/utils/arrayUtils';
import { ItemTypeCategoryWithContainer } from '@/utils/ItemTypes';

export type DepositClearingDataEntry = {
  itemTypeId: number;
  itemTypeName: string;
  itemTypeCategory: ItemTypeCategoryWithContainer;
  itemTypeImage?: string;
  delivered: number;
  returnDirty: number;
  returnClean: number;
  tooManyReturned: number;
  tooFewReturned: number;
  total: number;
};
export type InvoiceDataInput =
  | Omit<GetEventAccountStockQuery['getEventAccountStock'], '__typename'>
  | Omit<
      GetAllEventAccountStockQuery['getAllEventAccountsStock'],
      '__typename'
    >;

export async function getInvoiceData(
  data: InvoiceDataInput,
): Promise<Record<string, DepositClearingDataEntry[]>> {
  const billingData: Record<string, DepositClearingDataEntry[]> = {};

  if (data != null) {
    const containerTypeInfoById: Record<number, ContainerType> = {};
    for (const ct of data.container_types) {
      containerTypeInfoById[ct.id] = ct;
    }
    const itemLocations = data.item_location_stock;

    const containerTypes = data.container_types;
    const allContainerStationTypes = itemLocations
      .flatMap((il) => il.container_station_deliveries)
      .concat(itemLocations.flatMap((il) => il.container_station_returns))
      .map((d) => d.item_type)
      .filter((v, i, a) => distinctBy(v, i, a, 'id'))
      .sort((a, b) => a.id - b.id);

    const assetItemTypes = itemLocations
      .flatMap((il) => il.asset_deliveries.concat(il.asset_returns))
      .map((a) => ({
        id: a.item_type_id,
        name: a.item_type_name,
        image: a.item_type_image ?? undefined,
      }))
      .filter((v, i, a) => distinctBy(v, i, a, 'id'))
      .sort((a, b) => a.id - b.id);

    for (const itemLocation of itemLocations) {
      billingData[itemLocation.name] = [];
      for (const containerType of containerTypes) {
        const deliveryInfo = itemLocation.container_deliveries.find(
          (d) => d.container_type_id === containerType.id,
        );
        const returnInfo = itemLocation.container_returns.find(
          (d) => d.container_type_id === containerType.id,
        );

        const goodwill = itemLocation.goodwill.find(
          (d) => d.container_type_id === containerType.id,
        );

        const delivered = deliveryInfo?.amount || 0;
        const returnDirty = (returnInfo?.amount || 0) + (goodwill?.amount || 0);
        const returnClean =
          (returnInfo?.amount_sealed || 0) + (goodwill?.amount_sealed || 0);
        const total = delivered - returnDirty - returnClean;

        billingData[itemLocation.name].push({
          itemTypeId: containerType.id,
          itemTypeName: containerType.name,
          itemTypeCategory: 'CONTAINER',
          delivered,
          returnDirty,
          returnClean,
          tooManyReturned: total < 0 ? -total : 0,
          tooFewReturned: total > 0 ? total : 0,
          total,
        });
      }

      for (const containerStationType of allContainerStationTypes) {
        const deliveryInfo = itemLocation.container_station_deliveries.filter(
          (d) => d.item_type.id === containerStationType.id,
        );
        const returnInfo = itemLocation.container_station_returns.filter(
          (d) => d.item_type.id === containerStationType.id,
        );

        const delivered = deliveryInfo.length || 0;
        const returned = returnInfo.length || 0;
        const total = delivered - returned;
        billingData[itemLocation.name].push({
          itemTypeId: containerStationType.id,
          itemTypeName: containerStationType.name,
          itemTypeCategory: ItemTypeCategory.ContainerStation,
          delivered,
          returnDirty: returned,
          returnClean: 0,
          tooManyReturned: total < 0 ? -total : 0,
          tooFewReturned: total > 0 ? total : 0,
          total,
        });
      }

      for (const asset of assetItemTypes) {
        const delivered =
          itemLocation.asset_deliveries.find((a) => a.item_type_id === asset.id)
            ?.amount || 0;
        const returned =
          itemLocation.asset_returns.find((a) => a.item_type_id === asset.id)
            ?.amount || 0;
        const total = delivered - returned;
        billingData[itemLocation.name].push({
          itemTypeId: asset.id,
          itemTypeName: asset.name,
          itemTypeCategory: ItemTypeCategory.Asset,
          itemTypeImage: asset.image,
          delivered,
          returnDirty: returned,
          returnClean: 0,
          tooManyReturned: total < 0 ? -total : 0,
          tooFewReturned: total > 0 ? total : 0,
          total,
        });
      }
    }
  }
  return billingData;
}
